import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faArrowLeft,
  faClockRotateLeft,
  faLocationDot,
  faTowerBroadcast,
} from "@fortawesome/free-solid-svg-icons";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import LoadingOverlay from "../common/LoadingOverlay/LoadingOverlay";
import "./HistoryScreen.css";
import { getAuthToken } from "../../utils/authStorage";

const absoluteUrl = (url) => {
  if (!url) return "";
  if (url.startsWith("http")) return url;
  return window.__toAbsoluteUrl ? window.__toAbsoluteUrl(url) : url;
};

const formatDate = (value) => {
  if (!value) return "Unknown date";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
};

const CRITICAL_CLASSES = new Set([
  "AisladorRoto",
  "DescargaParcial",
  "ConductorDanado",
  "PiezaFaltante",
  "HerrajeSuelto",
  "TorreFisurada",
  "FijacionDefectuosa",
]);

const WARNING_CLASSES = new Set([
  "AisladorCorrosion",
  "HerrajeCorrosion",
  "ObjetoAjeno",
  "TorreCorrosion",
  "AisladorPolimericoErosionado",
  "AtaduraDefectuosa",
  "AisladorContaminado",
]);

const detectionColor = (className) => {
  if (CRITICAL_CLASSES.has(className)) return "#ef4444";
  if (WARNING_CLASSES.has(className)) return "#f59e0b";
  if (className === "AisladorContaminado") return "#eab308";
  return "#22c55e";
};

const getLocationStatus = (locationItem) => {
  const images = locationItem.images || [];
  const latestImage = images[images.length - 1];

  if (!latestImage?.analyzed) {
    return { key: "pending", label: "Not analyzed" };
  }

  const detectedClasses = Object.entries(latestImage.classCounts || {})
    .filter(([, count]) => count > 0)
    .map(([className]) => className);

  if (detectedClasses.some((className) => CRITICAL_CLASSES.has(className))) {
    return { key: "critical", label: "Critical" };
  }

  if (latestImage.totalDetections > 0) {
    return { key: "warning", label: "With detections" };
  }

  return { key: "clear", label: "No detections" };
};

function DetectionImage({ image, showDetections }) {
  const frameRef = useRef(null);
  const imageRef = useRef(null);
  const [imageRect, setImageRect] = useState(null);

  const updateImageRect = useCallback(() => {
    const frame = frameRef.current;
    const element = imageRef.current;
    if (!frame || !element || !element.naturalWidth || !element.naturalHeight) {
      return;
    }

    const scale = Math.min(
      frame.clientWidth / element.naturalWidth,
      frame.clientHeight / element.naturalHeight,
    );
    const width = element.naturalWidth * scale;
    const height = element.naturalHeight * scale;

    setImageRect({
      left: (frame.clientWidth - width) / 2,
      top: (frame.clientHeight - height) / 2,
      width,
      height,
      scale,
    });
  }, []);

  useEffect(() => {
    const frame = frameRef.current;
    if (!frame) return undefined;

    const observer = new ResizeObserver(updateImageRect);
    observer.observe(frame);
    updateImageRect();
    return () => observer.disconnect();
  }, [image?.imageId, updateImageRect]);

  return (
    <div className="history-image-frame" ref={frameRef}>
      <img
        ref={imageRef}
        src={absoluteUrl(image.originalImageUrl)}
        alt={image.imageName}
        onLoad={updateImageRect}
      />
      {showDetections && imageRect && (image.detections || []).map((detection, index) => {
        const box = detection.boundingBox;
        if (!box) return null;

        const color = detectionColor(detection.cls);
        return (
          <div
            className="history-detection-box"
            key={`${detection.cls}-${index}`}
            style={{
              borderColor: color,
              left: imageRect.left + box.x1 * imageRect.scale,
              top: imageRect.top + box.y1 * imageRect.scale,
              width: Math.max(1, (box.x2 - box.x1) * imageRect.scale),
              height: Math.max(1, (box.y2 - box.y1) * imageRect.scale),
            }}
          >
            <span style={{ backgroundColor: color }}>{detection.cls}</span>
          </div>
        );
      })}
    </div>
  );
}

function ImagePanel({ title, image, showAnalyzed, onToggleAnalyzed }) {
  if (!image) {
    return <div className="history-image-empty">Select an image</div>;
  }

  const canShowDetections = (image.detections || []).length > 0;

  return (
    <section className="history-image-panel">
      <div className="history-image-heading">
        <div>
          <span className="history-image-eyebrow">{title}</span>
          <h2>{formatDate(image.capturedAt)}</h2>
          <p>{image.inspectionName}</p>
        </div>
        {canShowDetections && (
          <label className="history-toggle">
            <input
              type="checkbox"
              checked={showAnalyzed}
              onChange={(event) => onToggleAnalyzed(event.target.checked)}
            />
            <span>Detections</span>
          </label>
        )}
      </div>

      <DetectionImage image={image} showDetections={showAnalyzed} />

      <div className="history-image-meta">
        <span>{image.imageName}</span>
        <strong>{image.totalDetections} detections</strong>
      </div>
    </section>
  );
}

export default function HistoryScreen() {
  const { projectId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const [locations, setLocations] = useState([]);
  const [selectedKey, setSelectedKey] = useState("");
  const [beforeId, setBeforeId] = useState("");
  const [afterId, setAfterId] = useState("");
  const [showBeforeAnalyzed, setShowBeforeAnalyzed] = useState(false);
  const [showAfterAnalyzed, setShowAfterAnalyzed] = useState(false);
  const [detectionFilter, setDetectionFilter] = useState("all");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const projectName = location.state?.projectName || "Project";

  useEffect(() => {
    const token = getAuthToken();
    setLoading(true);
    setError("");

    axios
      .get(`/api/projects/${projectId}/history/locations`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then(({ data }) => {
        const nextLocations = Array.isArray(data) ? data : [];
        setLocations(nextLocations);
        setSelectedKey(nextLocations[0]?.locationKey || "");
      })
      .catch((requestError) => {
        setError(
          requestError.response?.data?.message
          || requestError.response?.data?.error
          || "Could not load historical images.",
        );
      })
      .finally(() => setLoading(false));
  }, [projectId]);

  const selectedLocation = useMemo(
    () => locations.find((item) => item.locationKey === selectedKey) || null,
    [locations, selectedKey],
  );

  const filteredLocations = useMemo(() => locations.filter((item) => {
    if (detectionFilter === "all") return true;

    const images = item.images || [];
    const beforeHasDetections = (images[0]?.totalDetections || 0) > 0;
    const afterHasDetections = (images[images.length - 1]?.totalDetections || 0) > 0;

    if (detectionFilter === "before") return beforeHasDetections;
    if (detectionFilter === "after") return afterHasDetections;
    return beforeHasDetections && afterHasDetections;
  }), [locations, detectionFilter]);

  useEffect(() => {
    if (filteredLocations.some((item) => item.locationKey === selectedKey)) {
      return;
    }
    setSelectedKey(filteredLocations[0]?.locationKey || "");
  }, [filteredLocations, selectedKey]);

  useEffect(() => {
    const images = selectedLocation?.images || [];
    setBeforeId(images[0]?.imageId || "");
    setAfterId(images[images.length - 1]?.imageId || "");
    setShowBeforeAnalyzed(false);
    setShowAfterAnalyzed(false);
  }, [selectedLocation]);

  const beforeImage = useMemo(
    () => selectedLocation?.images?.find((image) => image.imageId === beforeId) || null,
    [selectedLocation, beforeId],
  );

  const afterImage = useMemo(
    () => selectedLocation?.images?.find((image) => image.imageId === afterId) || null,
    [selectedLocation, afterId],
  );

  const classChanges = useMemo(() => {
    const beforeCounts = beforeImage?.classCounts || {};
    const afterCounts = afterImage?.classCounts || {};
    const classes = new Set([...Object.keys(beforeCounts), ...Object.keys(afterCounts)]);

    return [...classes]
      .map((name) => ({
        name,
        before: beforeCounts[name] || 0,
        after: afterCounts[name] || 0,
        delta: (afterCounts[name] || 0) - (beforeCounts[name] || 0),
      }))
      .sort((a, b) => Math.abs(b.delta) - Math.abs(a.delta) || a.name.localeCompare(b.name));
  }, [beforeImage, afterImage]);

  return (
    <div className="history-page">
      <LoadingOverlay show={loading} text="Loading historical images..." />

      <header className="history-header">
        <div>
          <button
            className="history-back"
            type="button"
            onClick={() => navigate(`/projects/${projectId}`)}
          >
            <FontAwesomeIcon icon={faArrowLeft} />
            Back to project
          </button>
          <h1>Historical comparison</h1>
          <p>{projectName}</p>
        </div>
        <div className="history-summary">
          <FontAwesomeIcon icon={faClockRotateLeft} />
          <span>{locations.length} comparable locations</span>
        </div>
      </header>

      {error && <div className="history-error">{error}</div>}

      {!loading && !error && locations.length === 0 && (
        <div className="history-empty">
          <FontAwesomeIcon icon={faClockRotateLeft} />
          <h2>No historical matches yet</h2>
          <p>
            This project needs at least two images assigned to the same pole
            or captured within 10 meters of each other.
          </p>
        </div>
      )}

      {!loading && locations.length > 0 && (
        <div className="history-layout">
          <aside className="history-locations">
            <div className="history-section-heading">
              <h2>Locations</h2>
              <span>{filteredLocations.length} of {locations.length}</span>
            </div>

            <div className="history-location-filters" aria-label="Filter locations by detections">
              {[
                ["all", "All"],
                ["before", "Before"],
                ["after", "After"],
                ["both", "Both"],
              ].map(([value, label]) => (
                <button
                  type="button"
                  key={value}
                  className={detectionFilter === value ? "is-active" : ""}
                  onClick={() => setDetectionFilter(value)}
                >
                  {label}
                </button>
              ))}
            </div>

            <div className="history-location-list">
              {filteredLocations.map((item) => {
                const status = getLocationStatus(item);
                return (
                  <button
                    type="button"
                    key={item.locationKey}
                    className={`history-location-item history-location-item--${status.key} ${selectedKey === item.locationKey ? "is-active" : ""}`}
                    onClick={() => setSelectedKey(item.locationKey)}
                  >
                    <FontAwesomeIcon
                      icon={item.matchType === "POLE" ? faTowerBroadcast : faLocationDot}
                    />
                    <span className="history-location-content">
                      <span className="history-location-title">
                        <strong>{item.label}</strong>
                        <span className={`history-location-status is-${status.key}`}>
                          <span aria-hidden="true" />
                          {status.label}
                        </span>
                      </span>
                      <small>
                        {item.imageCount} images · {item.inspectionCount} inspections
                      </small>
                      <small>
                        {formatDate(item.firstCapturedAt)} – {formatDate(item.lastCapturedAt)}
                      </small>
                    </span>
                  </button>
                );
              })}
              {filteredLocations.length === 0 && (
                <div className="history-location-filter-empty">
                  No locations match this filter.
                </div>
              )}
            </div>
          </aside>

          {selectedLocation ? (
          <main className="history-comparison">
            <div className="history-comparison-head">
              <div>
                <span className="history-match-type">
                  {selectedLocation?.matchType === "POLE" ? "Matched by pole" : "Matched by GPS"}
                </span>
                <h2>{selectedLocation?.label}</h2>
              </div>

              <div className="history-selectors">
                <label>
                  Before
                  <select value={beforeId} onChange={(event) => setBeforeId(event.target.value)}>
                    {selectedLocation?.images?.map((image) => (
                      <option key={image.imageId} value={image.imageId}>
                        {formatDate(image.capturedAt)} · {image.inspectionName}
                      </option>
                    ))}
                  </select>
                </label>
                <label>
                  After
                  <select value={afterId} onChange={(event) => setAfterId(event.target.value)}>
                    {selectedLocation?.images?.map((image) => (
                      <option key={image.imageId} value={image.imageId}>
                        {formatDate(image.capturedAt)} · {image.inspectionName}
                      </option>
                    ))}
                  </select>
                </label>
              </div>
            </div>

            <div className="history-images">
              <ImagePanel
                title="Before"
                image={beforeImage}
                showAnalyzed={showBeforeAnalyzed}
                onToggleAnalyzed={setShowBeforeAnalyzed}
              />
              <ImagePanel
                title="After"
                image={afterImage}
                showAnalyzed={showAfterAnalyzed}
                onToggleAnalyzed={setShowAfterAnalyzed}
              />
            </div>

            <section className="history-findings">
              <div className="history-section-heading">
                <h2>Detection changes</h2>
                <span>{classChanges.length} classes</span>
              </div>

              {classChanges.length === 0 ? (
                <p className="history-findings-empty">No detections in the selected images.</p>
              ) : (
                <div className="history-findings-table">
                  <div className="history-findings-row is-header">
                    <span>Class</span>
                    <span>Before</span>
                    <span>After</span>
                    <span>Change</span>
                  </div>
                  {classChanges.map((item) => (
                    <div className="history-findings-row" key={item.name}>
                      <strong>{item.name}</strong>
                      <span>{item.before}</span>
                      <span>{item.after}</span>
                      <span className={item.delta > 0 ? "is-worse" : item.delta < 0 ? "is-better" : ""}>
                        {item.delta > 0 ? `+${item.delta}` : item.delta}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </section>
          </main>
          ) : (
            <main className="history-comparison history-comparison-empty">
              <h2>No matching locations</h2>
              <p>Choose another detection filter to continue comparing images.</p>
            </main>
          )}
        </div>
      )}
    </div>
  );
}
